import { useMemo } from 'react'
import { IfcModelMetadata } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import {
  getGuidToHiddenFromFilteredElements,
  getGuidToHiddenFromStoreyTypes,
  getGuidToHiddenFromTypes,
  getGuidToMGroup,
  getGuidToStorey,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { useControlStore } from '@editorStores'

/**
 * Map of IFC/GLB element guid -> hidden, matching exactly what `IFCGlbModel` hides in the scene:
 * type visibility, per-storey type visibility, hidden storeys, filtered elements (from the Filter
 * step) and manually hidden elements. Highlighted elements are forced visible. Shared so the scene
 * and the import-step preselection agree on what is currently visible.
 */
const useIfcGuidToHidden = (metadata: IfcModelMetadata | null): Record<string, boolean> => {
  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const glbTypeVisibilityByStorey = useControlStore(state => state.glbTypeVisibilityByStorey)
  const glbHiddenStoreys = useControlStore(state => state.glbHiddenStoreys)
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const highlightedIfcElementGuids = useControlStore(state => state.highlightedIfcElementGuids)
  const showFilteredIfcElements = useControlStore(state => state.showFilteredIfcElements)

  const guidToMGroup = useMemo(() => getGuidToMGroup(metadata), [metadata])
  const guidToStorey = useMemo(
    () => getGuidToStorey(metadata?.storeyAssignments),
    [metadata?.storeyAssignments],
  )

  const typeBasedHidden = useMemo(
    () => getGuidToHiddenFromTypes(guidToMGroup, glbTypeVisibility),
    [guidToMGroup, glbTypeVisibility],
  )
  const storeyTypeBasedHidden = useMemo(
    () => getGuidToHiddenFromStoreyTypes(guidToMGroup, guidToStorey, glbTypeVisibilityByStorey),
    [guidToMGroup, guidToStorey, glbTypeVisibilityByStorey],
  )
  const storeyBasedHidden = useMemo(() => {
    const out: Record<string, boolean> = {}
    for (const [guid, storey] of Object.entries(guidToStorey)) {
      if (glbHiddenStoreys[storey]) out[guid] = true
    }
    return out
  }, [guidToStorey, glbHiddenStoreys])
  const filteredElementHidden = useMemo(
    () =>
      showFilteredIfcElements
        ? {}
        : getGuidToHiddenFromFilteredElements(metadata?.filteredElements),
    [metadata?.filteredElements, showFilteredIfcElements],
  )

  return useMemo(() => {
    const baseHidden = Array.from(hiddenElementIds).reduce((acc, guid) => {
      acc[guid] = true
      return acc
    }, {} as Record<string, boolean>)

    const merged = {
      ...typeBasedHidden,
      ...storeyTypeBasedHidden,
      ...storeyBasedHidden,
      ...filteredElementHidden,
      ...baseHidden,
    }

    highlightedIfcElementGuids.forEach(guid => {
      merged[guid] = false
    })

    return merged
  }, [
    filteredElementHidden,
    hiddenElementIds,
    typeBasedHidden,
    storeyTypeBasedHidden,
    storeyBasedHidden,
    highlightedIfcElementGuids,
  ])
}

export default useIfcGuidToHidden
